import React, { useState } from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton, OutlinePillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { DEMO_PRESETS } from "@/mock";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Cpu, ShieldCheck, Zap, AlertCircle, ArrowRight, RefreshCw, MessageSquareCode } from "lucide-react";

export default function McpPlayground() {
  const [inputText, setInputText] = useState(DEMO_PRESETS[0].value);
  const [selectedPreset, setSelectedPreset] = useState(DEMO_PRESETS[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const applyPreset = (preset) => {
    setSelectedPreset(preset);
    setInputText(preset.value);
    setResult(null);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputText(val);
    // Find matching preset or set to custom
    const match = DEMO_PRESETS.find(p => p.value.toLowerCase() === val.trim().toLowerCase());
    if (match) {
      setSelectedPreset(match);
    } else {
      setSelectedPreset({
        id: "custom",
        label: "Custom Input",
        value: val,
        translation: "Translated contextually...",
        context: "Analyzed dynamically via rule-based heuristic token parsing.",
        tokens: val.split(/\s+/).flatMap(word => {
          if (word.length > 5) {
            return [word.slice(0, Math.floor(word.length / 2)), word.slice(Math.floor(word.length / 2))];
          }
          return [word];
        })
      });
    }
  };

  const processText = async () => {
    if (!inputText.trim()) {
      toast.error("Please enter a Swahili or Sheng phrase.");
      return;
    }
    setLoading(true);
    // Simulate pipeline latency
    await new Promise((r) => setTimeout(r, 450));
    setLoading(false);

    // Standard GPT Tokenizer would split much more aggressively
    const standardTokens = selectedPreset.tokens.flatMap(t => {
      if (t.length > 3) {
        return [t.slice(0, 2), t.slice(2, 4), t.slice(4)];
      }
      return [t];
    }).filter(Boolean);

    setResult({
      utuTokens: selectedPreset.tokens,
      standardTokens: standardTokens,
      translation: selectedPreset.translation,
      context: selectedPreset.context,
    });
    toast.success("Context pipeline completed successfully.");
  };

  return (
    <Layout>
      <SEO
        title="UTU MCP Interactive Playground"
        description="Interact with the UTU Model Context Protocol (MCP) server. Test Swahili, Sheng, and low-resource dialect segmentation and visualize tokenizer performance comparisons."
        url="/mcp/playground"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <Cpu className="h-4 w-4" />
            Flagship Interactive Tool
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4">
            UTU MCP Playground
          </h1>
          <p className="text-base text-white/70">
            Compare token efficiency and inspect the cultural context injection layer. Type a custom phrase or try the pre-loaded linguistic samples.
          </p>
        </div>

        {/* Preset Selector */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {DEMO_PRESETS.map((preset) => (
            <OutlinePillButton
              key={preset.id}
              onClick={() => applyPreset(preset)}
              className={`h-9 px-4 text-xs font-semibold ${
                selectedPreset?.id === preset.id
                  ? "border-[hsl(var(--utu-gold))] bg-amber-500/10 text-[hsl(var(--utu-gold))]"
                  : "border-white/10"
              }`}
            >
              {preset.label}
            </OutlinePillButton>
          ))}
        </div>

        {/* Interaction Panel */}
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start mb-16">
          <GlassCard className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold text-white/55 uppercase tracking-wider">Input Prompt</span>
              {selectedPreset?.id !== "custom" && (
                <span className="text-[10px] text-[hsl(var(--utu-gold))] font-semibold bg-amber-500/5 border border-amber-500/10 px-2 py-0.5 rounded">
                  {selectedPreset?.label} Active
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Input
                value={inputText}
                onChange={handleInputChange}
                placeholder="Type Swahili or Sheng slang (e.g. Form ni gani leo wadau?)..."
                className="h-12 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
              />
              <GoldPillButton
                onClick={processText}
                disabled={loading}
                className="h-12 px-6 shrink-0"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Process"}
                <ArrowRight className="h-4 w-4 ml-1" />
              </GoldPillButton>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <h4 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-2">Preset Description</h4>
                <div className="text-sm text-white/80 bg-white/5 p-4 rounded-xl border border-white/10">
                  {selectedPreset?.context}
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Results Panel */}
          <div>
            {!result ? (
              <GlassCard className="p-8 text-center border-dashed border-white/10 min-h-[300px] flex flex-col justify-center items-center">
                <MessageSquareCode className="h-12 w-12 text-white/35 mb-4 animate-pulse" />
                <h4 className="text-lg font-semibold text-white/80 mb-2">Await Ingestion</h4>
                <p className="text-xs text-white/55 max-w-[240px]">
                  Click the **Process** button to run the linguistic token analysis pipeline.
                </p>
              </GlassCard>
            ) : (
              <div className="space-y-6">
                {/* Pipeline Context Outputs */}
                <GlassCard className="p-6">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold text-xs uppercase tracking-wider mb-4">
                    <ShieldCheck className="h-4 w-4" />
                    Linguistic Memory Stabilized
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] text-white/50 uppercase tracking-wider">Semantic Meaning</div>
                      <div className="text-lg font-bold text-white mt-0.5">{result.translation}</div>
                    </div>

                    <div className="h-px bg-white/10" />

                    <div>
                      <div className="text-[10px] text-white/50 uppercase tracking-wider">Injected Knowledge nodes</div>
                      <p className="text-xs text-white/70 mt-1 leading-relaxed">{result.context}</p>
                    </div>
                  </div>
                </GlassCard>

                {/* Token Efficiency Comparison */}
                <GlassCard className="p-6">
                  <div className="flex items-center gap-2 text-[hsl(var(--utu-gold))] font-semibold text-xs uppercase tracking-wider mb-4">
                    <Zap className="h-4 w-4" />
                    Tokenization Efficiency
                  </div>

                  <div className="space-y-4 text-xs">
                    {/* UTU Tokenizer */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-white">UTU Segmenter (Agglutinative Aware)</span>
                        <span className="font-bold text-emerald-400">{result.utuTokens.length} Tokens</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 p-3 bg-emerald-400/5 border border-emerald-400/10 rounded-lg">
                        {result.utuTokens.map((t, i) => (
                          <span key={i} className="px-2 py-1 bg-emerald-400/15 border border-emerald-400/20 text-emerald-200 rounded font-mono text-[10px]">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Standard GPT Tokenizer */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-white/70">Standard BPE (Aggressive Fragmenter)</span>
                        <span className="font-bold text-red-400">{result.standardTokens.length} Tokens</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 p-3 bg-red-400/5 border border-red-400/10 rounded-lg opacity-80">
                        {result.standardTokens.map((t, i) => (
                          <span key={i} className="px-2 py-1 bg-red-400/15 border border-red-400/20 text-red-200 rounded font-mono text-[10px]">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Cost Metrics */}
                    <div className="bg-white/5 p-3 rounded-lg border border-white/10 flex justify-between items-center">
                      <span className="text-white/60">Estimated Context Overhead Saved:</span>
                      <span className="font-bold text-emerald-400 text-sm">
                        {Math.max(0, Math.round(((result.standardTokens.length - result.utuTokens.length) / result.standardTokens.length) * 100))}%
                      </span>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
          </div>
        </div>

        {/* Explanation Card */}
        <GlassCard className="p-8 md:p-10 mb-8">
          <h3 className="text-2xl font-bold text-white mb-4">Why Agglutinative Segmentation Matters</h3>
          <p className="text-sm text-white/65 leading-relaxed mb-4">
            Agglutinative languages (like Swahili) merge pronouns, tenses, prepositions, and roots into single composite words. Standard BPE tokenizers, trained on large English datasets, break these words into dozens of meaningless fragments. This leads to:
          </p>
          <div className="grid gap-6 md:grid-cols-3 text-xs mb-4">
            <div className="p-4 border border-white/5 bg-white/[0.02] rounded-lg">
              <h5 className="font-bold text-white mb-1">Inflated API Invoices</h5>
              <p className="text-white/60">Paying 400% more than English equivalent phrases due to aggressive subword fragmenting.</p>
            </div>
            <div className="p-4 border border-white/5 bg-white/[0.02] rounded-lg">
              <h5 className="font-bold text-white mb-1">Degraded Comprehension</h5>
              <p className="text-white/60">LLMs lose semantic tracking when word chunks are separated incorrectly, causing hallucinations.</p>
            </div>
            <div className="p-4 border border-white/5 bg-white/[0.02] rounded-lg">
              <h5 className="font-bold text-white mb-1">Cultural Erasure</h5>
              <p className="text-white/60">Local slang nuances and historical cultural connections are completely ignored by generic systems.</p>
            </div>
          </div>
        </GlassCard>
      </section>
    </Layout>
  );
}
